import { useEffect, useState } from "react";
import { UserPlus, Users, UserCheck, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import AccessOption from "./AccessOption";
import {
  organizationSettingsService,
  TicketAccessMode,
} from "@/services/organizationSettings.service/organizationSettings.api";

type AccessOptionType = "anyone" | "registered" | "approved";

const modeToOption = (m: TicketAccessMode): AccessOptionType => {
  if (m === "open") return "anyone";
  if (m === "hybrid") return "approved";
  return "registered";
};

const optionToMode = (o: AccessOptionType): TicketAccessMode => {
  if (o === "anyone") return "open";
  if (o === "approved") return "hybrid";
  return "restricted";
};

export default function TicketAccessControl() {
  const [selectedOption, setSelectedOption] = useState<AccessOptionType>("anyone");
  const [clientPortalUrl, setClientPortalUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [data, err] = await organizationSettingsService.getSettings();
      if (cancelled) return;
      if (err || !data) {
        toast.error(err?.msg || "Não foi possível carregar as configurações");
        setLoading(false);
        return;
      }
      setSelectedOption(modeToOption(data.ticketAccessMode));
      setClientPortalUrl(data.clientPortalUrl || "");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const [, err] = await organizationSettingsService.updateTicketAccessMode(optionToMode(selectedOption));
    setSaving(false);
    if (err) {
      toast.error(err.msg || "Erro ao salvar");
      return;
    }
    toast.success("Configurações de acesso atualizadas com sucesso!");
  };

  const handleCopyClientUrl = async () => {
    if (!clientPortalUrl) return;
    try {
      await navigator.clipboard.writeText(clientPortalUrl);
      toast.success("URL copiada!");
    } catch {
      toast.error("Não foi possível copiar a URL");
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground py-8">Carregando…</div>;
  }

  return (
    <div className="grid gap-6">
      <div className="grid gap-4 md:grid-cols-3">
        <AccessOption
          title="Acesso Aberto"
          description="Qualquer cliente pode abrir chamado"
          icon={<UserPlus className="h-5 w-5" />}
          bgClass="from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900"
          benefits={[
            "Sem barreiras para novos clientes",
            "Aumente a base de atendimentos",
            "Ideal para suporte geral",
          ]}
          isSelected={selectedOption === "anyone"}
          onClick={() => setSelectedOption("anyone")}
        />

        <AccessOption
          title="Acesso Restrito"
          description="Somente pessoas cadastradas nos grupos"
          icon={<Users className="h-5 w-5" />}
          bgClass="from-indigo-50 to-indigo-100 dark:from-indigo-950 dark:to-indigo-900"
          benefits={[
            "Maior controle sobre quem abre chamados",
            "Identificação garantida dos solicitantes",
            "Ideal para times internos",
          ]}
          isSelected={selectedOption === "registered"}
          onClick={() => setSelectedOption("registered")}
        />

        <AccessOption
          title="Acesso Híbrido"
          description="Apenas pessoas aprovadas podem abrir chamados"
          icon={<UserCheck className="h-5 w-5" />}
          bgClass="from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900"
          benefits={[
            "Balanço entre abertura e controle",
            "Processo de aprovação sob demanda",
            "Ideal para clientes externos selecionados",
          ]}
          isSelected={selectedOption === "approved"}
          onClick={() => setSelectedOption("approved")}
        />
      </div>

      <div className="rounded-lg border border-border p-4">
        <p className="text-sm font-medium mb-2">URL de acesso do cliente</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input readOnly value={clientPortalUrl || "URL indisponível"} />
          <Button type="button" variant="outline" onClick={handleCopyClientUrl} disabled={!clientPortalUrl}>
            Copiar
          </Button>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="gap-2">
          <Shield className="h-4 w-4" />
          {saving ? "Salvando…" : "Salvar Configuração"}
        </Button>
      </div>
    </div>
  );
}
